import * as React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  Row,
  ExpandedState,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { Input } from "components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import TablePagination from "./table-pagination";
import { useEffect, useState } from "react";
import {
  getProductDataAction,
  productDeleteAction,
  ProductList,
  productSetAvailabilityAction,
} from "action/product-action";
import { toast } from "sonner";
import Loading from "components/loading";
import EditProductComponent, {
  EditProductProps,
} from "screens/partials/edit-product";
import { Checkbox } from "components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Button } from "components/ui/button";
import {
  CircleCheck,
  Eye,
  PackageCheck,
  SquarePen,
  Trash2,
} from "lucide-react";
import PageHeader from "components/page-header";
import { Card } from "components/ui/card";
import { Badge } from "components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
// Import CSSTransition and TransitionGroup for animations
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "./StorboltProductListingComponent.css";
import { CURRENCY } from "helpers/constants";
import { useSessionControl } from "hooks/use-session-control";
import { CircleMinus, Megaphone } from "lucide-react";
import { useOpenBoost } from "lib/boost";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import BulkProductImport from "./bulk-product-import";

const StorboltProductListingComponent = () => {
  const openBoost = useOpenBoost();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [data, setData] = React.useState<ProductList[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductList | null>(
    null,
  );
  const [showList, setShowList] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedProductsId, setSelectedProductsId] = useState<string[]>([]);
  const [productAvailability, setProductAvailability] = useState<string>("");
  const { sessionControl } = useSessionControl();

  useEffect(() => {
    setIsLoading(true);
    getProductDataAction()
      .then((response) => {
        if (response.status === 200) {
          setData(response.body);
          if (response.message) {
            toast.error(response.message);
          }
        } else {
          sessionControl(response.status);
          toast.error(response.error);
        }
        setIsLoading(false);
      })
      .catch((err: any) => {
        toast.error("Failed to get products, An error occurred");
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderView = (product: ProductList) => {
    const editProps: EditProductProps = {
      id: product.id as string,
      name: product.name,
      category: product.category,
      price: product.price,
      description: product.description,
      featured: product.featured,
      today_special: product.today_special,
      available_after_days: (product as any).available_after_days,
      image_url: product.image_url,
      options: (product as any).options,
    };
    return (
      <EditProductComponent
        {...editProps}
        onCancel={() => setShowList(true)}
        onUpdateDone={() => window.location.reload()}
      />
    );
  };

  const handleDeleteProduct = (product: ProductList) => {
    setSelectedProduct(product);
    setShowDeleteDialog(true);
  };

  const confirmDeleteProduct = () => {
    if (selectedProduct) {
      const toastId = toast.loading("Deleting product...");
      productDeleteAction(selectedProduct.id).then((response) => {
        if (response.status === 200) {
          toast.success("Product deleted successfully", {
            id: toastId,
          });
        } else {
          sessionControl(response.status);
          toast.error(response.error, {
            id: toastId,
          });
        }
      });
      setData((prevData) =>
        prevData.filter((item) => item.id !== selectedProduct.id),
      );
      setShowDeleteDialog(false);
      setSelectedProduct(null);
    }
  };

  const cancelDeleteProduct = () => {
    setShowDeleteDialog(false);
    setSelectedProduct(null);
  };

  const handleAvailabilityRequest = (
    productIds: string[],
    isAvailable: boolean,
  ) => {
    const toastId = toast.loading("Updating availability...");
    return productSetAvailabilityAction(productIds, isAvailable).then(
      (response) => {
        if (response.status === 200) {
          toast.success("Availability updated successfully", {
            id: toastId,
          });
        } else {
          sessionControl(response.status);
          toast.error(response.error, {
            id: toastId,
          });
        }
      },
    );
  };

  const handleAvailabilityUpdate = async () => {
    if (productAvailability) {
      const availability = productAvailability === "available"; // true if "available", false if "unavailable"

      await handleAvailabilityRequest(selectedProductsId, availability);

      setData((prevData) =>
        prevData.map((item) =>
          selectedProductsId.includes(item.id)
            ? availability
              ? { ...item, available: availability }
              : {
                  ...item,
                  featured: availability,
                  today_special: availability,
                  available: availability,
                }
            : item,
        ),
      );

      setProductAvailability("");
    }
  };

  /** A pill for made-to-order products, e.g. "Ready in 3 days"; nothing for products that are ready now. */
  const readyPill = (product: ProductList) => {
    const days = product.available_after_days;
    if (typeof days !== "number" || days <= 0) return null;
    return (
      <Badge className="bg-info/10 text-info text-[11px] normal-case">
        Ready in {days} {days === 1 ? "day" : "days"}
      </Badge>
    );
  };

  const readyText = (product: ProductList) => {
    const days = product.available_after_days;
    return typeof days === "number" && days > 0
      ? `Made to order, ready ${days} ${days === 1 ? "day" : "days"} after ordering`
      : "Ready now";
  };

  const setProductSelected = (row: Row<ProductList>, value: boolean) => {
    row.toggleSelected(value);
    if (value) {
      setSelectedProductsId([...selectedProductsId, row.original.id]);
    } else {
      setSelectedProductsId(
        selectedProductsId.filter((id) => id !== row.original.id),
      );
    }
  };

  const columns: ColumnDef<ProductList>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
            if (value) {
              setSelectedProductsId(
                table.getRowModel().rows.map((row) => row.original.id),
              );
            } else {
              setSelectedProductsId([]);
            }
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <div className="xl:w-16">
          <Checkbox
            onClick={(e) => {
              e.stopPropagation();
            }}
            checked={row.getIsSelected()}
            onCheckedChange={(value) => setProductSelected(row, !!value)}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <span className="flex flex-wrap items-center gap-2">
          {row.getValue("name")}
          {readyPill(row.original)}
        </span>
      ),
    },
    {
      accessorKey: "price",
      header: "Price",
      cell: ({ row }) => {
        return (
          <span>
            {CURRENCY}
            {row.getValue("price")}
          </span>
        );
      },
    },
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ row }) => <span>{row.getValue("category")}</span>,
    },
    {
      accessorKey: "featured",
      header: () => (
        <span className="flex items-center justify-center">Featured</span>
      ),
      cell: ({ row }) => {
        return (
          <span className="flex items-center justify-center">
            {row.getValue("featured") ? (
              <CircleCheck className="w-4 h-4 text-success" />
            ) : (
              <CircleMinus className="w-4 h-4 text-destructive" />
            )}
          </span>
        );
      },
    },
    {
      accessorKey: "today_special",
      header: () => (
        <span className="flex items-center justify-center">Today Special</span>
      ),
      cell: ({ row }) => {
        return (
          <span className="flex items-center justify-center">
            {row.getValue("today_special") ? (
              <CircleCheck className="w-4 h-4 text-success" />
            ) : (
              <CircleMinus className="w-4 h-4 text-destructive" />
            )}
          </span>
        );
      },
    },
    {
      accessorKey: "available",
      header: () => (
        <span className="flex items-center justify-center">Available</span>
      ),
      cell: ({ row }) => {
        return (
          <span className="flex items-center justify-center">
            {row.getValue("available") ? (
              <PackageCheck className="w-4 h-4 text-success" />
            ) : (
              <CircleMinus className="w-4 h-4 text-destructive" />
            )}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Action",
      enableHiding: false,
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 border-default-300 text-default-700"
              onClick={(e) => {
                e.stopPropagation();
                openBoost(product.id);
              }}
            >
              <Megaphone className="h-3.5 w-3.5" />
              Boost
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-7 h-7 ring-offset-transparent border-default-300 text-default-500"
                    color="secondary"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>View</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-7 h-7 ring-offset-transparent border-default-300 text-default-500"
                    color="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProduct(product);
                      setShowList(false);
                    }}
                  >
                    <SquarePen className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Edit</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-7 h-7 ring-offset-transparent border-default-300 text-default-500"
                    color="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProduct(product);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="bg-destructive text-destructive-foreground"
                >
                  <p>Delete</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      expanded,
    },
  });

  const toggleRowExpansion = (rowId: string) => {
    table.setExpanded((old) => ({
      ...(old as { [key: string]: boolean }),
      [rowId]: !old[rowId],
    }));
  };

  return (
    <>
      {showList ? (
        <div className="w-full">
          <PageHeader
            title="Listed Products"
            actions={
              <BulkProductImport onImported={() => window.location.reload()} />
            }
          />

          <Card className="overflow-hidden">
            {/* Toolbar: search on the left, bulk availability on the right. */}
            <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between md:px-5">
              <div className="w-full md:max-w-xs">
                <Input
                  placeholder="Search..."
                  value={
                    (table.getColumn("name")?.getFilterValue() as string) ?? ""
                  }
                  onChange={(event) =>
                    table.getColumn("name")?.setFilterValue(event.target.value)
                  }
                  size="md"
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1 md:w-56 md:flex-none">
                  <Select
                    value={productAvailability || ""}
                    onValueChange={(value) => setProductAvailability(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Product Availability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Mark as Available</SelectItem>
                      <SelectItem value="unavailable">
                        Mark as Unavailable
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleAvailabilityUpdate}
                  disabled={
                    productAvailability === "" || selectedProductsId.length === 0
                  }
                  size="md"
                >
                  Set
                </Button>
              </div>
            </div>

            {/* Phones: one card per product. */}
            <div className="divide-y md:hidden">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const product = row.original;
                  return (
                    <div key={row.id} className="p-4" data-state={row.getIsSelected() && "selected"}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          className="mt-1"
                          checked={row.getIsSelected()}
                          onCheckedChange={(value) => setProductSelected(row, !!value)}
                          aria-label="Select row"
                        />
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-start"
                          onClick={() => toggleRowExpansion(row.id)}
                          aria-expanded={row.getIsExpanded()}
                        >
                          <div className="truncate font-medium text-default-900">{product.name}</div>
                          <div className="mt-0.5 text-sm text-default-600">
                            {product.category} · {CURRENCY}
                            {product.price}
                          </div>
                        </button>
                        <div className="flex flex-none items-center gap-1">
                          <Button
                            variant="ghost"
                            color="secondary"
                            size="icon"
                            className="h-9 w-9"
                            aria-label="Edit"
                            onClick={() => {
                              setSelectedProduct(product);
                              setShowList(false);
                            }}
                          >
                            <SquarePen className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            color="secondary"
                            size="icon"
                            className="h-9 w-9"
                            aria-label="Delete"
                            onClick={() => handleDeleteProduct(product)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5 ps-7">
                        <Badge
                          className={
                            product.available
                              ? "bg-success/10 text-success text-[11px]"
                              : "bg-default-100 text-destructive text-[11px]"
                          }
                        >
                          {product.available ? "Available" : "Unavailable"}
                        </Badge>
                        {product.featured && (
                          <Badge className="bg-sage text-sage-foreground text-[11px]">Featured</Badge>
                        )}
                        {product.today_special && (
                          <Badge className="bg-sage text-sage-foreground text-[11px]">Today Special</Badge>
                        )}
                        {readyPill(product)}
                        <Button
                          variant="outline"
                          size="sm"
                          className="ms-auto h-7 gap-1.5 border-default-300 text-default-700"
                          onClick={() => openBoost(product.id)}
                        >
                          <Megaphone className="h-3.5 w-3.5" />
                          Boost on Meta Ads
                        </Button>
                      </div>
                      {row.getIsExpanded() && (
                        <div className="mt-3 space-y-1 rounded-[14px] bg-default-50 p-3 ps-7 text-sm text-default-700">
                          {product.image_url && (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="mb-2 h-24 w-24 rounded-[10px] object-cover"
                            />
                          )}
                          <p>
                            <strong>When is it ready:</strong> {readyText(product)}
                          </p>
                          <p>
                            <strong>Description:</strong> {product.description}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : isLoading ? (
                <div className="p-6">
                  <Loading />
                </div>
              ) : (
                <div className="p-10 text-center text-sm text-default-600">
                  No Items In Your Inventory.
                </div>
              )}
            </div>

            <div className="hidden md:block">
          <Table>
            <TableHeader className="bg-default-50 [&_tr]:border-t-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      data-state={row.getIsSelected() && "selected"}
                      onClick={() => toggleRowExpansion(row.id)}
                      className="cursor-pointer"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="h-[75px]">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TransitionGroup component={null}>
                      {row.getIsExpanded() ? (
                        <CSSTransition
                          key={row.id}
                          timeout={300}
                          classNames="expand"
                        >
                          <TableRow>
                            <TableCell colSpan={columns.length}>
                              {/* Expanded content */}
                              <div className="p-4">
                                <div className="flex gap-4">
                                  <img
                                    src={row.original.image_url}
                                    alt={row.original.name}
                                    className="w-32 h-32 object-cover"
                                  />
                                  <div>
                                    <h3 className="text-lg font-bold">
                                      {row.original.name}
                                    </h3>
                                    <p>
                                      <strong>Category:</strong>{" "}
                                      {row.original.category}
                                    </p>
                                    <p>
                                      <strong>Price:</strong> {CURRENCY}
                                      {row.original.price}
                                    </p>
                                    <p>
                                      <strong>Featured:</strong>{" "}
                                      {row.original.featured ? "Yes" : "No"}
                                    </p>
                                    <p>
                                      <strong>Today's Deal:</strong>{" "}
                                      {row.original.today_special
                                        ? "Yes"
                                        : "No"}
                                    </p>
                                    <p>
                                      <strong>When is it ready:</strong>{" "}
                                      {readyText(row.original)}
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-4">
                                  <strong>Description:</strong>
                                  <p>{row.original.description}</p>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        </CSSTransition>
                      ) : null}
                    </TransitionGroup>
                  </React.Fragment>
                ))
              ) : isLoading ? (
                <TableRow>
                  <TableCell className="h-[75px]" colSpan={columns.length}>
                    <Loading />
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No Items In Your Inventory.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
          <div className="border-t px-4 md:px-5">
            <TablePagination table={table} />
          </div>
          </Card>

          {/* Delete Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Product</DialogTitle>
              </DialogHeader>
              <p>Are you sure you want to delete this product?</p>
              <DialogFooter>
                <Button variant="outline" onClick={cancelDeleteProduct}>
                  Cancel
                </Button>
                <Button variant="outline" onClick={confirmDeleteProduct}>
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        selectedProduct && renderView(selectedProduct)
      )}
    </>
  );
};

export default StorboltProductListingComponent;
